import { Box, Collapse, Flex, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ProposalDiffLine } from '../chat/types';

type Props = {
  lines: ProposalDiffLine[];
  truncated?: boolean;
  defaultCollapsed?: boolean;
};

const LINE_STYLES = {
  added: { background: 'var(--mantine-color-success-0)', color: 'var(--mantine-color-success-9)' },
  removed: { background: 'var(--mantine-color-red-0)', color: 'var(--mantine-color-red-9)' },
  context: { background: 'transparent', color: 'var(--mantine-color-gray-7)' },
} as const;

const LINE_MARKERS = { added: '+', removed: '−', context: ' ' } as const;

export function ProposalDiffBlock({ lines, truncated, defaultCollapsed }: Props) {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  if (lines.length === 0) return null;

  const diff = (
    <Box
      p="xs"
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        border: '1px solid var(--mantine-color-gray-3)',
        background: 'var(--mantine-color-white)',
        overflowX: 'auto',
      }}
    >
      {lines.map((line, index) => (
        <Text
          key={index}
          size="textXs"
          ff="monospace"
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            ...LINE_STYLES[line.type],
          }}
        >
          {LINE_MARKERS[line.type]} {line.text}
        </Text>
      ))}
      {truncated && (
        <Text size="textXs" c="gray.6" fs="italic" mt="xxs">
          Changes truncated for display
        </Text>
      )}
    </Box>
  );

  if (!defaultCollapsed) return diff;

  return (
    <Box>
      <UnstyledButton onClick={() => setExpanded(!expanded)}>
        <Flex align="center" gap="xxs">
          {expanded ? (
            <IconChevronDown size={14} color="var(--mantine-color-gray-7)" aria-hidden />
          ) : (
            <IconChevronRight size={14} color="var(--mantine-color-gray-7)" aria-hidden />
          )}
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.7">
            {expanded ? 'Hide changes' : 'Show changes'}
          </Text>
        </Flex>
      </UnstyledButton>
      {/* Children are rendered conditionally (sacrificing the close animation) so a large
          collapsed diff stays out of the DOM entirely. */}
      <Collapse expanded={expanded}>{expanded ? <Box mt="xxs">{diff}</Box> : null}</Collapse>
    </Box>
  );
}
